import React, { useState, useEffect, useMemo, useRef } from 'react'
import ReactQuill from 'react-quill-new'
import 'react-quill-new/dist/quill.snow.css'
import styles from './TextEditor.module.css'

/** Faz o upload e devolve a URL a inserir no `src`; `null` quando falhou (quem envia já avisou o usuário). */
export type UploadImagemEditor = (file: File) => Promise<string | null>

export const TIPOS_IMAGEM_EDITOR = 'image/png,image/jpeg,image/webp'

interface ITextEditor {
  value: string
  onChange: (content: string) => void
  disabled?: boolean
  height?: number | string
  placeholder?: string
  /** Presente, a toolbar ganha o botão de imagem; a imagem entra como `<img src>` da URL devolvida, nunca base64. */
  onUploadImage?: UploadImagemEditor
}

export const TextEditor: React.FC<ITextEditor> = ({
  onChange,
  value,
  disabled = false,
  height = 300,
  placeholder = 'Digite sua anotação aqui...',
  onUploadImage,
}) => {
  const [editorContent, setEditorContent] = useState(value || '')
  const quillRef = useRef<ReactQuill>(null)
  // O handler fica fixo nos módulos (recriar os módulos remonta o Quill); a função mais recente vem pelo ref.
  const uploadRef = useRef(onUploadImage)
  uploadRef.current = onUploadImage
  const comImagem = Boolean(onUploadImage)

  useEffect(() => {
    setEditorContent(value)
  }, [value])

  const handleEditorChange = (content: string) => {
    const sanitizedValue = content === '<p><br></p>' ? '' : content

    setEditorContent(sanitizedValue)
    onChange(sanitizedValue)
  }

  const modules = useMemo(() => {
    const container = [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike', 'blockquote'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ align: [] }],
      comImagem ? ['link', 'image', 'clean'] : ['link', 'clean'],
    ]
    if (!comImagem) return { toolbar: container }

    const escolherImagem = () => {
      const entrada = document.createElement('input')
      entrada.type = 'file'
      entrada.accept = TIPOS_IMAGEM_EDITOR
      entrada.onchange = async () => {
        const arquivo = entrada.files?.[0]
        const quill = quillRef.current?.getEditor()
        if (!arquivo || !quill || !uploadRef.current) return
        const url = await uploadRef.current(arquivo)
        if (!url) return
        const posicao = quill.getSelection(true)?.index ?? quill.getLength()
        quill.insertEmbed(posicao, 'image', url, 'user')
        quill.setSelection(posicao + 1, 0)
      }
      entrada.click()
    }
    return { toolbar: { container, handlers: { image: escolherImagem } } }
  }, [comImagem])

  return (
    <div
      className={`d-flex flex-column flex-grow-1 position-relative ${styles.editor}`}
      style={{ height: typeof height === 'number' ? `${height}px` : height }}
    >
      <ReactQuill
        ref={quillRef}
        theme='snow'
        value={editorContent}
        onChange={handleEditorChange}
        placeholder={placeholder}
        className='h-100'
        style={{
          paddingBottom: '50px',
        }}
        readOnly={disabled}
        modules={modules}
      />
    </div>
  )
}
